import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: "#050505" }}>
        <div className="w-full max-w-sm text-center">
          <div className="border border-[#1A1A1A] bg-[#0D0D0D] p-12">
            <Mail className="h-12 w-12 text-[#39FF14] mx-auto mb-6" strokeWidth={1.5} />
            <h1 data-testid="reset-email-sent" className="text-2xl font-bold uppercase tracking-tight text-[#F2F2F2] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
              Check Your Email
            </h1>
            <p className="text-[#A1A1A1] text-sm mb-6">
              If an account exists for <span className="text-[#39FF14]">{email}</span>, we sent a password reset link. Check your inbox and spam folder.
            </p>
            <Link to="/login">
              <Button className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6">
                Back to Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: "#050505" }}>
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-3">Account</p>
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
            Forgot Password
          </h1>
          <p className="text-[#A1A1A1] text-sm mt-3">Enter your email and we'll send you a reset link.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-password-form">
          <div className="space-y-2">
            <Label htmlFor="email" className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Email</Label>
            <Input
              id="email"
              data-testid="forgot-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:ring-1 focus:ring-[#39FF14] placeholder:text-[#A1A1A1]/50"
              placeholder="your@email.com"
            />
          </div>

          {error && (
            <div className="p-3 border border-red-500/30 bg-red-500/10 text-red-400 text-sm" data-testid="forgot-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid="forgot-submit"
            disabled={loading}
            className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6 disabled:opacity-50"
          >
            {loading ? "Sending..." : "Send Reset Link"}
          </Button>
        </form>

        <p className="mt-6 text-[#A1A1A1] text-sm">
          Remember your password?{" "}
          <Link to="/login" data-testid="back-to-login" className="text-[#39FF14] hover:underline font-mono text-xs uppercase tracking-wider">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
